import React, { useRef } from 'react';
import { useMenuBuilder } from '../../context/MenuBuilderContext';
import { Button } from '../ui/button';
import { Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export const ImageUploader = ({ className }) => {
  const { menuData, updateMenuData } = useMenuBuilder();
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Only JPEG and PNG images are allowed');
      return;
    }

    if (file.size > 2097152) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    // Create local blob URL for immediate preview
    const previewUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const validSizes = [
        { width: 2500, height: 1686 },
        { width: 2500, height: 843 },
        { width: 1200, height: 810 },
        { width: 1200, height: 405 }
      ];
      const isValidSize = validSizes.some(
        size => img.width === size.width && img.height === size.height
      );
      if (!isValidSize) {
        toast.warning(
          `Image dimensions (${img.width}×${img.height}) don't match LINE requirements. Recommended: 2500×1686 or 2500×843`,
          { duration: 5000 }
        );
      }

      // Store blob URL for preview and File for upload on save
      updateMenuData({
        imageUrl: previewUrl,
        pendingImageFile: file,
        size: { width: img.width, height: img.height }
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    img.onerror = () => {
      URL.revokeObjectURL(previewUrl);
      toast.error('Failed to read image');
    };
    img.src = previewUrl;
  };

  const handleRemoveImage = () => {
    // Revoke blob URL if it's a local preview
    if (menuData.imageUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(menuData.imageUrl);
    }
    updateMenuData({ imageUrl: null, pendingImageFile: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.info('Image removed');
  };

  return (
    <div className={cn("glass-panel rounded-lg p-4", className)} data-testid="image-uploader">
      <p className="label-text mb-3">Menu Image</p>

      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="image-input"
      />

      {menuData.imageUrl ? (
        <div className="space-y-3">
          <div className="relative rounded-lg overflow-hidden border border-[#27272A]">
            <img
              src={menuData.imageUrl}
              alt="Menu preview"
              className="w-full h-auto"
            />
            <Button
              size="icon"
              variant="destructive"
              className="absolute top-2 right-2 w-8 h-8"
              onClick={handleRemoveImage}
              data-testid="remove-image-btn"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-[#52525B]">
            {menuData.size.width} × {menuData.size.height}px
          </p>
        </div>
      ) : (
        <Button
          variant="outline"
          className="w-full h-24 border-dashed border-[#27272A] hover:border-[#00E05E]/50 hover:bg-[#00E05E]/5"
          onClick={() => fileInputRef.current?.click()}
          data-testid="upload-btn"
        >
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-6 h-6 text-[#52525B]" />
            <span className="text-sm text-[#A1A1AA]">Upload Image</span>
            <span className="text-xs text-[#52525B]">JPEG/PNG, max 2MB</span>
          </div>
        </Button>
      )}

      <div className="mt-3 text-xs text-[#52525B]">
        <p>Recommended sizes:</p>
        <p className="font-mono">2500 × 1686 (Full)</p>
        <p className="font-mono">2500 × 843 (Compact)</p>
      </div>
    </div>
  );
};

export default ImageUploader;
